import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSelectedRestaurant } from '../../context/SelectedRestaurantContext';
import { tablesService, Table, CreateTableData, TableWaiterAssignment } from '../../services/tables';
import { waitersService, Waiter } from '../../services/waiters';
import { userStorage } from '../../services/userStorage';
import { Listbox } from '@headlessui/react';
import ConfirmModal from '../../components/ConfirmModal';
import ErrorModal from '../../components/ErrorModal';
import { ApiError } from '../../services/http';

// Componente de icono QR inspirado en la imagen, gris y sin fondo
const QRIcon = ({ className = "w-12 h-12" }) => (
  <svg
    className={className}
    viewBox="0 0 100 100"
    fill="none"
    stroke="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Marcadores de esquina */}
    <rect x="5" y="5" width="25" height="25" stroke="#444" strokeWidth="3" fill="none"/>
    <rect x="13" y="13" width="9" height="9" fill="#444" />
    <rect x="70" y="5" width="25" height="25" stroke="#444" strokeWidth="3" fill="none"/>
    <rect x="78" y="13" width="9" height="9" fill="#444" />
    <rect x="5" y="70" width="25" height="25" stroke="#444" strokeWidth="3" fill="none"/>
    <rect x="13" y="78" width="9" height="9" fill="#444" />

    {/* Patrón caótico */}
    <rect x="40" y="5" width="6" height="6" fill="#888"/>
    <rect x="55" y="5" width="6" height="6" fill="#888"/>
    <rect x="35" y="20" width="6" height="6" fill="#888"/>
    <rect x="50" y="20" width="6" height="6" fill="#888"/>
    <rect x="65" y="20" width="6" height="6" fill="#888"/>
    <rect x="40" y="35" width="6" height="6" fill="#888"/>
    <rect x="55" y="35" width="6" height="6" fill="#888"/>
    <rect x="35" y="50" width="6" height="6" fill="#888"/>
    <rect x="50" y="50" width="6" height="6" fill="#888"/>
    <rect x="65" y="50" width="6" height="6" fill="#888"/>
    <rect x="40" y="65" width="6" height="6" fill="#888"/>
    <rect x="55" y="65" width="6" height="6" fill="#888"/>
    <rect x="35" y="80" width="6" height="6" fill="#888"/>
    <rect x="50" y="80" width="6" height="6" fill="#888"/>
    <rect x="65" y="80" width="6" height="6" fill="#888"/>
  </svg>
);

const statusColors = {
  active: '#22b6b2',
  inactive: '#9CA3AF',
  maintenance: '#9CA3AF',
  occupied: '#f59e0b',
};

const statusLabels = {
  active: 'Disponible',
  inactive: 'Inactiva',
  maintenance: 'Mantenimiento',
  occupied: 'Ocupada',
};

const locationLabels = {
  interior: 'Interior',
  exterior: 'Exterior',
  barra: 'Barra',
  vip: 'VIP',
  terraza: 'Terraza',
};

const locationIcons = {
  interior: '🏠',
  exterior: '🌳',
  barra: '🍺',
  vip: '👑',
  terraza: '🌅',
};

// Componente para generar QR
const QRGenerator = ({ table, onClose }: { table: Table; onClose: () => void }) => {
  const [qrImage, setQrImage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [errorModal, setErrorModal] = useState<{ open: boolean; title?: string; message: string; details?: string }>({ open: false, message: '' });
  
  const generateQR = async () => {
    setIsGenerating(true);
    try {
      const user = await userStorage.getUser();
      console.log('🔍 Debug user from storage:', user);
      if (!user?.id) {
        throw new ApiError('Usuario no autenticado', 401);
      }

      // Crear datos de la mesa para el QR (igual al proyecto original)
      // const tableInfo = {
      //   tableId: table.id,
      //   tableNumber: table.number,
      //   capacity: table.capacity,
      //   location: table.location,
      //   status: table.status,
      //   restaurantId: table.restaurantId,
      //   restaurantName: table.restaurantName || '',
      //   createdAt: new Date().toISOString(),
      //   lastOrder: null
      // };
      
      // Codificar en base64 (igual al proyecto original)
      //const jsonString = JSON.stringify(tableInfo);
      //const base64Data = btoa(jsonString);
      
      // Obtener el token de la mesa
      const response = await tablesService.getTableTokenByTableId(user.id, table.restaurantId, table.id);
      console.log('🔍 Response from backend:', response);
      if (response) {
        // Codificar el token JWT en base64 para el QR
        const base64Token = btoa(JSON.stringify({ token_mesa: response }));

        const qrData = `${import.meta.env.VITE_APP_ROOT_URL}/menu?table=${base64Token}`;
        console.log('🔍 QR Data URL:', qrData);
        console.log('🔍 Token JWT:', response);
        console.log('🔍 Token Base64:', base64Token);
        // Generar QR usando API externa TODO: en lugar de llamar a esta api ... generar el qr localmente
        const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrData)}&format=png&margin=10&color=000000&bgcolor=FFFFFF`;
        
        setQrImage(qrUrl);
      }
    } catch (error: any) {
      console.error('Error generando QR:', error);
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        setErrorModal({ open: true, title: 'No se pudo generar el QR', message: error.message });
      } else {
        setErrorModal({ open: true, title: 'Error del servidor', message: 'No se pudo generar el QR' });
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadQR = async () => {
    if (!qrImage) return;
    
    try {
      const response = await fetch(qrImage);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mesa-${table.number}-qr.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error descargando QR:', error);
      setErrorModal({ open: true, title: 'Descarga fallida', message: 'No se pudo descargar el QR' });
    }
  };

  const getLocationIcon = (location: string) => {
    switch (location) {
      case 'interior': return '🏠';
      case 'exterior': return '🌳';
      case 'barra': return '🍺';
      case 'vip': return '👑';
      case 'terraza': return '🌅';
      default: return '📍';
    }
  };

  useEffect(() => {
    generateQR();
  }, [table]);

  return (
    <div className="fixed inset-0 bg-slate-700/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999] animate-in fade-in duration-300">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl mx-2 p-0 relative max-h-[95vh] flex flex-col border border-white/20">
        {/* Fondo animado */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#22B6B2]/5 via-[#2C4358]/5 to-[#85929e]/5 animate-pulse"></div>
        
        {/* Header fijo */}
        <div className="relative z-20 bg-gradient-to-r from-[#22B6B2]/10 to-[#2C4358]/10 backdrop-blur-md border-b border-[#22B6B2]/20 px-6 sm:px-8 py-6 rounded-t-3xl">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/80 backdrop-blur-md hover:bg-white flex items-center justify-center text-[#2C4358] hover:text-[#22B6B2] text-2xl transition-all duration-300 z-20 group shadow-lg"
            aria-label="Cerrar"
          >
            <span className="group-hover:rotate-90 transition-transform duration-300">✕</span>
          </button>
          <div className="text-center">
            <h2 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#22B6B2] to-[#2C4358] mb-2 tracking-tight">
              Mesa {table.number}
            </h2>
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-[#22B6B2] rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-[#2C4358] rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
              <div className="w-2 h-2 bg-[#85929e] rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
            </div>
          </div>
        </div>
        
        {/* Contenido scrolleable con borde redondeado real para la barra de scroll */}
        <div className="overflow-hidden rounded-b-3xl flex-1 flex flex-col">
          <div className="relative z-10 px-8 pt-8 pb-0 space-y-8 overflow-y-auto h-full">
            {/* Información de la mesa futurista */}
            <div className="bg-gradient-to-r from-[#22B6B2]/10 to-[#2C4358]/10 border-2 border-[#22B6B2]/20 rounded-2xl p-6">           
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">      
                <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 border border-[#22B6B2]/20">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-[#22B6B2] to-[#2C4358] rounded-lg flex items-center justify-center text-white text-sm font-bold">
                      📍
                    </div>
                    <div>
                      <div className="text-sm text-[#85929e] font-medium">Ubicación</div>
                      <div className="text-lg font-bold text-[#2C4358] flex items-center gap-2">
                        <span className="text-xl">{getLocationIcon(table.location)}</span>
                        <span className="capitalize">{table.location}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 border border-[#22B6B2]/20">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-[#22B6B2] to-[#2C4358] rounded-lg flex items-center justify-center text-white text-sm font-bold">
                      👥
                    </div>
                    <div>
                      <div className="text-sm text-[#85929e] font-medium">Capacidad</div>
                      <div className="text-lg font-bold text-[#2C4358]">{table.capacity} personas</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sección del QR futurista */}
            <div className="bg-gradient-to-r from-[#22B6B2]/10 to-[#2C4358]/10 border-2 border-[#22B6B2]/20 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-[#2C4358] mb-6 flex items-center gap-3">
                <span className="text-2xl">📱</span>
                Código QR
              </h3>
              
              <div className="text-center">
                {isGenerating ? (
                  <div className="py-12">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#22B6B2] mx-auto mb-6"></div>
                    <p className="text-[#85929e] text-lg font-medium">Generando QR...</p>
                  </div>
                ) : qrImage ? (
                  <div className="space-y-8">
                    <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border border-[#22B6B2]/20 shadow-xl">
                      <img src={qrImage} alt="QR Code" className="mx-auto rounded-xl shadow-lg" />
                    </div>
                    
                    {/* Botón de descarga futurista */}
                    <button
                      onClick={downloadQR}
                      className="py-4 px-8 bg-gradient-to-r from-[#22B6B2]/20 to-[#2C4358]/20 text-[#22B6B2] hover:from-[#22B6B2]/30 hover:to-[#2C4358]/30 rounded-2xl transition-all duration-300 font-bold text-lg border-2 border-[#22B6B2]/40 hover:border-[#22B6B2]/60 hover:scale-105 relative overflow-hidden group/btn flex items-center gap-3 mx-auto"
                    >
                      {/* Efecto de brillo */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700"></div>
                      <span className="relative z-10 text-xl">📥</span>
                      <span className="relative z-10">Descargar QR</span>
                    </button>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Información adicional futurista */}
            <div className="bg-gradient-to-r from-[#22B6B2]/10 to-[#2C4358]/10 border-2 border-[#22B6B2]/20 rounded-2xl p-6">
              <h4 className="text-lg font-bold text-[#2C4358] mb-4 flex items-center gap-3">
                <span className="text-xl">ℹ️</span>
                Información
              </h4>
              <div className="space-y-3 text-sm">
                <div className="flex items-center space-x-3">
                  <span className="w-2 h-2 bg-[#22B6B2] rounded-full"></span>
                  <span className="text-[#2C4358] font-medium">Los clientes escanean el QR para acceder al menú</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="w-2 h-2 bg-[#2C4358] rounded-full"></span>
                  <span className="text-[#2C4358] font-medium">Cada mesa tiene un código único</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="w-2 h-2 bg-[#85929e] rounded-full"></span>
                  <span className="text-[#2C4358] font-medium">Se puede imprimir y colocar en la mesa</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="w-2 h-2 bg-[#22B6B2] rounded-full"></span>
                  <span className="text-[#2C4358] font-medium">Funciona en cualquier dispositivo móvil</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ErrorModal isOpen={errorModal.open} title={errorModal.title} message={errorModal.message} details={errorModal.details} onClose={() => setErrorModal({ open: false, message: '' })} />
    </div>
  );
};

// Modal mejorado para agregar/editar mesa
const TableModal = ({ 
  visible, 
  onClose, 
  onSubmit, 
  editingTable, 
  newTableData, 
  setNewTableData 
}: {
  visible: boolean;
  onClose: () => void;
  onSubmit: () => void;
  editingTable: Table | null;
  newTableData: CreateTableData;
  setNewTableData: (data: CreateTableData) => void;
}) => {
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [errorModal, setErrorModal] = useState<{ open: boolean; title?: string; message: string; details?: string }>({ open: false, message: '' });

  const locations = [
    { key: 'interior', label: 'Interior', icon: '🏠' },
    { key: 'exterior', label: 'Exterior', icon: '🌳' },
    { key: 'barra', label: 'Barra', icon: '🍺' },
    { key: 'vip', label: 'VIP', icon: '👑' },
    { key: 'terraza', label: 'Terraza', icon: '🌅' },
  ];

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!newTableData.number || newTableData.number <= 0) {
      newErrors.number = 'Número de mesa requerido y debe ser positivo';
    }
    
    if (!newTableData.capacity || newTableData.capacity <= 0) {
      newErrors.capacity = 'Capacidad requerida y debe ser positiva';
    }
    
    if (!newTableData.location) {
      newErrors.location = 'Selecciona ubicación';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit();
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg mx-2 relative max-h-[90vh] overflow-hidden border border-gray-100">
        {/* Header elegante */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <span className="text-white text-lg">🪑</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                {editingTable ? 'Editar' : 'Crear'} Mesa
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="w-10 h-10 rounded-full bg-white hover:bg-gray-50 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-all duration-200 shadow-sm border border-gray-200 hover:shadow-md"
              aria-label="Cerrar"
            >
              <span className="text-xl">×</span>
            </button>
          </div>
        </div>
        
        <form onSubmit={e => { e.preventDefault(); handleSubmit(); }} className="p-8 space-y-8">
          {/* Número y capacidad */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Número de Mesa *
              </label>
              <div className="relative">
                <input
                  autoFocus
                  type="number"
                  value={newTableData.number || ''}
                  onChange={(e) => {
                    setNewTableData({...newTableData, number: parseInt(e.target.value) || 0});
                    if (errors.number) setErrors({...errors, number: ''});
                  }}
                  className={`w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-200 ${
                    errors.number ? 'border-red-300 bg-red-50 focus:ring-red-100 focus:border-red-500' : 'hover:border-gray-300'
                  }`}
                  placeholder="1"
                  min="1"
                  required
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <span className="text-sm">#</span>
                </div>
              </div>
              {errors.number && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <span className="text-red-500">⚠️</span>
                  <span>{errors.number}</span>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Capacidad *
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={newTableData.capacity || ''}
                  onChange={(e) => {
                    setNewTableData({...newTableData, capacity: parseInt(e.target.value) || 0});
                    if (errors.capacity) setErrors({...errors, capacity: ''});
                  }}
                  className={`w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-200 ${
                    errors.capacity ? 'border-red-300 bg-red-50 focus:ring-red-100 focus:border-red-500' : 'hover:border-gray-300'
                  }`}
                  placeholder="4"
                  min="1"
                  required
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <span className="text-sm">👥</span>
                </div>
              </div>
              {errors.capacity && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <span className="text-red-500">⚠️</span>
                  <span>{errors.capacity}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Ubicación mejorada */}
          <div className="space-y-4">
            <label className="block text-sm font-semibold text-gray-700 mb-4">
              Ubicación *
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {locations.map((loc) => (
                <button
                  key={loc.key}
                  type="button"
                  onClick={() => {
                    setNewTableData({...newTableData, location: loc.key as any});
                    if (errors.location) setErrors({...errors, location: ''});
                  }}
                  className={`group p-4 rounded-2xl border-2 transition-all duration-300 text-center relative overflow-hidden ${
                    newTableData.location === loc.key
                      ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100 text-blue-700 shadow-lg shadow-blue-100'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50 hover:shadow-md'
                  }`}
                >
                  {/* Efecto de brillo sutil */}
                  <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 transition-all duration-700 ${
                    newTableData.location === loc.key ? 'translate-x-full' : '-translate-x-full group-hover:translate-x-full'
                  }`}></div>
                  
                  <div className="relative z-10">
                    <div className={`text-3xl mb-2 transition-transform duration-300 ${
                      newTableData.location === loc.key ? 'scale-110' : 'group-hover:scale-105'
                    }`}>
                      {loc.icon}
                    </div>
                    <div className="text-sm font-semibold">{loc.label}</div>
                  </div>
                </button>
              ))}
            </div>
            {errors.location && (
              <div className="flex items-center gap-2 text-sm text-red-600">
                <span className="text-red-500">⚠️</span>
                <span>{errors.location}</span>
              </div>
            )}
          </div>
          
          {/* Botones mejorados */}
          <div className="flex gap-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 font-semibold border border-gray-200 hover:border-gray-300 hover:shadow-sm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
            >
              {editingTable ? 'Actualizar Mesa' : 'Crear Mesa'}
            </button>
          </div>
        </form>
      </div>
      <ErrorModal isOpen={errorModal.open} title={errorModal.title} message={errorModal.message} details={errorModal.details} onClose={() => setErrorModal({ open: false, message: '' })} />
    </div>
  );
};

// Modal de asignación de meseros
const AssignmentModal = ({ 
  visible, 
  onClose, 
  onSubmit, 
  table, 
  waiters, 
  selectedWaiterId, 
  setSelectedWaiterId, 
  assigning 
}: {
  visible: boolean;
  onClose: () => void;
  onSubmit: () => void;
  table: Table | null;
  waiters: Waiter[];
  selectedWaiterId: string;
  setSelectedWaiterId: (id: string) => void;
  assigning: boolean;
}) => {
  const [errorModal, setErrorModal] = useState<{ open: boolean; title?: string; message: string; details?: string }>({ open: false, message: '' });
  const navigate = useNavigate();

  if (!visible || !table) return null;

  return (
    <div className="fixed inset-0 bg-slate-700/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999] animate-in fade-in duration-300">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl mx-2 p-0 relative max-h-[95vh] flex flex-col border border-white/20">
        {/* Fondo animado */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#22B6B2]/5 via-[#2C4358]/5 to-[#85929e]/5 animate-pulse"></div>
        {/* Header fijo */}
        <div className="relative z-20 bg-gradient-to-r from-[#22B6B2]/10 to-[#2C4358]/10 backdrop-blur-md border-b border-[#22B6B2]/20 px-6 sm:px-8 py-6 rounded-t-3xl">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/80 backdrop-blur-md hover:bg-white flex items-center justify-center text-[#2C4358] hover:text-[#22B6B2] text-2xl transition-all duration-300 z-20 group shadow-lg"
            aria-label="Cerrar"
          >
            <span className="group-hover:rotate-90 transition-transform duration-300">✕</span>
          </button>
          <div className="text-center">
            <h2 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#22B6B2] to-[#2C4358] mb-2 tracking-tight">
              Asignar Mesero
            </h2>
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-[#22B6B2] rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-[#2C4358] rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
              <div className="w-2 h-2 bg-[#85929e] rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
            </div>
          </div>
        </div>
        {/* Body scrolleable con borde redondeado */}
        <div className="overflow-hidden rounded-b-3xl flex-1 flex flex-col">
          <div className="relative z-10 p-8 space-y-8 overflow-y-auto h-full">

            {/* Selector de mesero futurista */}
            <div className="space-y-4">
              <label className="block text-xl font-bold text-[#2C4358] flex items-center gap-3">
                <span className="text-2xl">👤</span>
                Seleccionar Mesero
              </label>
              <Listbox value={selectedWaiterId} onChange={setSelectedWaiterId} disabled={assigning}>
                <div className="relative z-20">
                  <Listbox.Button className="w-full px-6 py-5 bg-white border-2 border-[#22B6B2]/30 rounded-2xl text-lg font-medium text-[#2C4358] focus:outline-none focus:border-[#22B6B2] focus:bg-white transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between">
                    <span>
                      {selectedWaiterId
                        ? (waiters.find(w => w.id === selectedWaiterId)?.fullName || 'Mesero seleccionado')
                        : 'Selecciona un mesero'}
                    </span>
                    {/* Flecha */}
                    <svg className="w-5 h-5 text-[#85929e] ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </Listbox.Button>
                  <Listbox.Options className="absolute z-50 mt-2 w-full bg-white border-2 border-[#22B6B2]/30 rounded-2xl shadow-2xl max-h-60 overflow-auto focus:outline-none">
                    {waiters.length === 0 && (
                      <div className="px-6 py-5 text-center">
                        <div className="text-[#85929e] font-medium mb-3">No hay meseros disponibles</div>
                        <button
                          type="button"
                          onClick={() => { onClose(); navigate('/admin/waiters'); }}
                          className="inline-flex items-center px-4 py-2 bg-[#f7ebd6] text-[#2C4358] rounded-xl font-semibold border-2 border-[#f7ebd6]/50 hover:border-[#f0e0c0] hover:bg-[#f0e0c0] transition-colors"
                        >
                          ➕ Crear meseros
                        </button>
                      </div>
                    )}
                    {waiters.map((waiter) => (
                      <Listbox.Option
                        key={waiter.id}
                        value={waiter.id}
                        disabled={waiter.registrationStatus === 'pending'}
                        className={({ active, selected, disabled }) =>
                          `cursor-pointer select-none px-6 py-4 text-lg transition-all duration-300 ${
                            active ? 'bg-gradient-to-r from-[#22B6B2]/10 to-[#2C4358]/10 text-[#2C4358]' : 'text-[#2C4358]'
                          } ${selected ? 'font-bold bg-gradient-to-r from-[#22B6B2]/20 to-[#2C4358]/20' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`
                        }
                      >
                        {({ selected }) => (
                          <div className="flex items-center">
                            <span className="flex-1">
                              {waiter.fullName}
                              {waiter.registrationStatus === 'pending' && (
                                <span className="text-[#85929e] ml-2 font-medium">(Pendiente)</span>
                              )}
                            </span>
                            {selected && (
                              <svg className="w-5 h-5 text-[#22B6B2] ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </div>
              </Listbox>
            </div>

            {/* Información de la mesa futurista */}
            <div className="bg-gradient-to-r from-[#22B6B2]/10 to-[#2C4358]/10 border-2 border-[#22B6B2]/20 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-[#2C4358] mb-4 flex items-center gap-3">
                <span className="text-2xl">🪑</span>
                Mesa {table.number}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 border border-[#22B6B2]/20">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-[#22B6B2] to-[#2C4358] rounded-lg flex items-center justify-center text-white text-sm font-bold">
                      👥
                    </div>
                    <div>
                      <div className="text-sm text-[#85929e] font-medium">Capacidad</div>
                      <div className="text-lg font-bold text-[#2C4358]">{table.capacity} personas</div>
                    </div>
                  </div>
                </div>
                <div className="bg-white/80 backdrop-blur-md rounded-xl p-4 border border-[#22B6B2]/20">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-[#22B6B2] to-[#2C4358] rounded-lg flex items-center justify-center text-white text-sm font-bold">
                      📍
                    </div>
                    <div>
                      <div className="text-sm text-[#85929e] font-medium">Ubicación</div>
                      <div className="text-lg font-bold text-[#2C4358] capitalize">{table.location}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Botones futuristas */}
        <div className="relative z-10 bg-gradient-to-r from-[#22B6B2]/10 to-[#2C4358]/10 backdrop-blur-md border-t border-[#22B6B2]/20 px-6 sm:px-8 py-6">
          <div className="flex gap-4">
            <button
              onClick={onClose}
              disabled={assigning}
              className="flex-1 py-4 px-6 bg-gradient-to-r from-gray-500/10 to-gray-600/10 text-gray-700 hover:from-gray-500/20 hover:to-gray-600/20 rounded-2xl transition-all duration-300 font-bold text-lg border-2 border-gray-500/30 hover:border-gray-500/50 hover:scale-105 relative overflow-hidden group/btn disabled:opacity-50"
            >
              {/* Efecto de brillo */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700"></div>
              <span className="relative z-10">Cancelar</span>
            </button>
            <button
              onClick={onSubmit}
              disabled={!selectedWaiterId || assigning}
              className="flex-1 py-4 px-6 bg-gradient-to-r from-[#22B6B2]/20 to-[#2C4358]/20 text-[#22B6B2] hover:from-[#22B6B2]/30 hover:to-[#2C4358]/30 rounded-2xl transition-all duration-300 font-bold text-lg border-2 border-[#22B6B2]/40 hover:border-[#22B6B2]/60 hover:scale-105 relative overflow-hidden group/btn disabled:opacity-50 flex items-center justify-center gap-3"
            >
              {/* Efecto de brillo */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700"></div>
              {assigning ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#22B6B2]"></div>
                  <span className="relative z-10">Asignando...</span>
                </>
              ) : (
                <>
                  <span className="relative z-10 text-xl">👤</span>
                  <span className="relative z-10">Asignar Mesero</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      <ErrorModal isOpen={errorModal.open} title={errorModal.title} message={errorModal.message} details={errorModal.details} onClose={() => setErrorModal({ open: false, message: '' })} />
    </div>
  );
};

const TableCard = ({ 
  table, 
  onEdit, 
  onToggleStatus, 
  onDelete,
  onViewQR,
  onAssignWaiter
}: {
  table: Table;
  onEdit: (table: Table) => void;
  onToggleStatus: (table: Table) => void;
  onDelete: (table: Table) => void;
  onViewQR: (table: Table) => void;
  onAssignWaiter: (table: Table) => void;
}) => {
  const [errorModal, setErrorModal] = useState<{ open: boolean; title?: string; message: string; details?: string }>({ open: false, message: '' });
  const canPromptPay = table.status === 'plan_limited' && (userStorage.isOwner() || userStorage.isManager());

  const isOccupied = (table as any).is_occupied === true || table.status === 'occupied' || (table.waiters && table.waiters.length > 0);
  const effectiveStatus = isOccupied ? 'occupied' : table.status;
  return (
    <div className={`relative rounded-xl sm:rounded-2xl border border-gray-200 shadow-lg overflow-hidden hover:shadow-2xl transition-shadow flex flex-col h-full ${
      effectiveStatus === 'active' 
        ? 'bg-white' 
        : effectiveStatus === 'inactive'
        ? 'bg-gray-100' 
        : effectiveStatus === 'plan_limited'
        ? 'bg-white opacity-60 saturate-50'
        : effectiveStatus === 'occupied'
        ? 'bg-amber-50'
        : 'bg-gray-50'
    }`}>
      <div className="p-3 sm:p-5 flex-1 flex flex-col">
        {/* Header con información principal y botones de acción - Responsive */}
        <div className="flex items-start justify-between mb-3 sm:mb-4">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            {/* Imagen de la mesa o mesa con mesero en el header */}
            <div className={`rounded-lg p-2 sm:p-3 flex-shrink-0 ${
              effectiveStatus === 'active' 
                ? 'bg-indigo-100' 
                : effectiveStatus === 'inactive'
                ? 'bg-gray-200' 
                : effectiveStatus === 'occupied'
                ? 'bg-amber-100'
                : 'bg-gray-300' // mantenimiento
            }`}>
              <img 
                src={isOccupied ? "/assets/images/mesitaocupada.png" : "/assets/images/mesita.png"}
                alt="Mesa"
                className="w-8 h-8 sm:w-12 sm:h-12 object-contain"
                style={{ 
                  filter: effectiveStatus === 'active' ? 'none' : 
                          effectiveStatus === 'inactive' ? 'grayscale(100%)' : 
                          effectiveStatus === 'occupied' ? 'none' :
                          'grayscale(100%) brightness(0.5)' // mantenimiento
                }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className={`text-base sm:text-lg font-bold truncate ${
                effectiveStatus === 'active' 
                  ? 'text-gray-800' 
                  : effectiveStatus === 'inactive'
                  ? 'text-gray-600' 
                  : effectiveStatus === 'occupied'
                  ? 'text-amber-800'
                  : 'text-gray-500' // mantenimiento
              }`}>
                Mesa {table.number}
              </h3>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mt-1">
                <span 
                  className="px-2 py-1 text-xs font-medium rounded-full text-white w-fit"
                  style={{ backgroundColor: statusColors[table.status as keyof typeof statusColors] || '#9CA3AF' }}
                >
                  {statusLabels[table.status as keyof typeof statusLabels] || 'Desconocido'}
                </span>
                <span className={`text-xs ${
                  effectiveStatus === 'active' 
                    ? 'text-gray-600' 
                    : effectiveStatus === 'inactive'
                    ? 'text-gray-500' 
                    : effectiveStatus === 'occupied'
                    ? 'text-amber-700'
                    : 'text-gray-400' // mantenimiento
                }`}>
                  {locationIcons[table.location as keyof typeof locationIcons] || '📍'} {locationLabels[table.location as keyof typeof locationLabels] || 'Desconocido'}
                </span>
              </div>
            </div>
          </div>
          {/* Iconos de editar, eliminar y capacidad - Responsive */}
          <div className="flex items-center gap-1 sm:gap-2 ml-2 sm:ml-4 flex-shrink-0">
            <button
              onClick={() => onEdit(table)}
              className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
                effectiveStatus === 'active' 
                  ? 'text-gray-600 hover:text-teal-600 hover:bg-teal-50' 
                  : effectiveStatus === 'inactive'
                  ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-200' 
                  : effectiveStatus === 'occupied'
                  ? 'text-amber-700 hover:text-amber-800 hover:bg-amber-100'
                  : 'text-gray-300 hover:text-gray-500 hover:bg-gray-200' // mantenimiento
              }`}
              title="Editar mesa"
            >
              <span className="text-sm sm:text-base">✏️</span>
            </button>
            <button
              onClick={() => onDelete(table)}
              className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
                effectiveStatus === 'active' 
                  ? 'text-gray-600 hover:text-red-600 hover:bg-red-50' 
                  : effectiveStatus === 'inactive'
                  ? 'text-gray-400 hover:text-red-500 hover:bg-red-100' 
                  : effectiveStatus === 'occupied'
                  ? 'text-amber-700 hover:text-red-700 hover:bg-amber-100'
                  : 'text-gray-300 hover:text-red-400 hover:bg-red-50' // mantenimiento
              }`}
              title="Eliminar mesa"
            >
              <span className="text-sm sm:text-base">🗑️</span>
            </button>
            <div 
              className={`text-xs sm:text-sm font-medium ${
                effectiveStatus === 'active' 
                  ? 'text-gray-600' 
                  : effectiveStatus === 'inactive'
                  ? 'text-gray-500' 
                  : effectiveStatus === 'occupied'
                  ? 'text-amber-700'
                  : 'text-gray-400' // mantenimiento
              }`}
              title="Capacidad"
            >
              <span className="hidden sm:inline">{table.capacity} personas</span>
              <span className="sm:hidden">{table.capacity}p</span>
            </div>
          </div>
        </div>
        
        {/* Información adicional: aquí va la lista de meseros asignados - Responsive */}
        <div className={`rounded-lg p-2 sm:p-3 mb-3 sm:mb-4 ${
          effectiveStatus === 'active' 
            ? 'bg-gray-50' 
            : effectiveStatus === 'inactive'
            ? 'bg-gray-200' 
            : effectiveStatus === 'occupied'
            ? 'bg-amber-50'
            : 'bg-gray-300' // mantenimiento
        }`}>
          <div className="flex items-center gap-2 sm:gap-4 justify-center">
            {table.waiters && table.waiters.length === 0 ? (
              <span className="text-yellow-500 text-sm sm:text-lg flex items-center gap-1">
                <span>⚠️</span>
                <span className="hidden sm:inline">Sin mesero asignado</span>
                <span className="sm:hidden">Sin mesero</span>
              </span>
            ) : (
              table.waiters?.map((waiter: any, idx: number) => (
                <img
                  key={waiter.id || idx}
                  src="/assets/images/mesero-check.png"
                  alt="Mesero asignado"
                  className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border-4 border-green-400 bg-white shadow-lg"
                  title={waiter.fullName || 'Mesero asignado'}
                />
              ))
            )}
          </div>
        </div>
        
        {/* Botones principales de acción - Responsive */}
        <div className="mt-auto pt-3 sm:pt-4 flex gap-1.5 sm:gap-2">
          <button
            onClick={() => onViewQR(table)}
            className={`flex-1 py-2 sm:py-3 px-2 sm:px-4 rounded-lg text-2xl sm:text-3xl font-medium transition-colors flex items-center justify-center border border-gray-300 bg-white hover:border-gray-500 hover:bg-gray-50 shadow-sm ${
              effectiveStatus === 'active' 
                ? 'text-gray-700 hover:text-gray-900' 
                : effectiveStatus === 'inactive'
                ? 'text-gray-500 hover:text-gray-700' 
                : effectiveStatus === 'occupied'
                ? 'text-amber-700 hover:text-amber-800'
                : 'text-gray-400 hover:text-gray-600' // mantenimiento
            }`}
            title="Ver QR"
          >
            <QRIcon className="w-8 h-8 sm:w-10 sm:h-10" />
          </button>
          <button
            onClick={() => onAssignWaiter(table)}
            className={`flex-1 py-2 sm:py-3 px-2 sm:px-4 rounded-lg text-2xl sm:text-3xl font-medium transition-colors flex items-center justify-center border border-gray-300 bg-white hover:border-purple-500 hover:bg-purple-50 shadow-sm ${
              effectiveStatus === 'active' 
                ? 'text-purple-700 hover:text-purple-900' 
                : effectiveStatus === 'inactive'
                ? 'text-purple-500 hover:text-purple-700' 
                : effectiveStatus === 'occupied'
                ? 'text-purple-700 hover:text-purple-900'
                : 'text-purple-400 hover:text-purple-600' // mantenimiento
            }`}
            title="Asignar Mesero"
          >
            <img src="/assets/images/mesero.png" alt="Asignar mesero" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
          </button>
          <button
            onClick={() => onToggleStatus(table)}
            className={`flex-1 py-2 sm:py-3 px-2 sm:px-4 rounded-lg text-2xl sm:text-3xl font-medium transition-colors flex items-center justify-center border border-gray-300 bg-white hover:border-blue-500 hover:bg-blue-50 shadow-sm ${
              effectiveStatus === 'active' 
                ? 'text-blue-700 hover:text-blue-900' 
                : effectiveStatus === 'inactive'
                ? 'text-green-700 hover:text-green-900' 
                : effectiveStatus === 'occupied'
                ? 'text-amber-700 hover:text-amber-800'
                : 'text-yellow-700 hover:text-yellow-900' // mantenimiento
            }`}
            title={effectiveStatus === 'active' ? 'Desactivar mesa' : 'Activar mesa'}
          >
            {effectiveStatus === 'active' ? '⏸️' : '▶️'}
          </button>
        </div>
      </div>
      {canPromptPay && (
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-md border border-[#22B6B2]/30 rounded-2xl shadow-2xl px-6 py-4 text-center">
            <div className="text-base font-bold text-[#2C4358] mb-2">Mesa limitada por plan</div>
            <button
              onClick={() => window.location.href = '/pricing'}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#22B6B2] to-[#2C4358] text-white font-bold hover:opacity-90 transition"
            >
              Actualizar plan
            </button>
          </div>
        </div>
      )}
      <ErrorModal isOpen={errorModal.open} title={errorModal.title} message={errorModal.message} details={errorModal.details} onClose={() => setErrorModal({ open: false, message: '' })} />
    </div>
  );
};

export default function TablesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedRestaurantId } = useSelectedRestaurant();
  const [tables, setTables] = useState<Table[]>([]);
  const [waiters, setWaiters] = useState<Waiter[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedTableForQR, setSelectedTableForQR] = useState<Table | null>(null);
  const [selectedTableForAssignment, setSelectedTableForAssignment] = useState<Table | null>(null);
  const [selectedWaiterId, setSelectedWaiterId] = useState<string>('');
  const [assigning, setAssigning] = useState(false);
  const [editingTable, setEditingTable] = useState<Table | null>(null);
  const [newTableData, setNewTableData] = useState<CreateTableData>({
    number: 0,
    capacity: 4,
    location: 'interior',
  });
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [errorModal, setErrorModal] = useState<{ open: boolean; title?: string; message: string; details?: string }>({ open: false, message: '' });
  const [confirmViewQR, setConfirmViewQR] = useState<{ open: boolean; table: Table | null }>({ open: false, table: null });

  useEffect(() => {
    if (user?.uid && selectedRestaurantId) {
      loadTables();
      loadWaiters();
    }
  }, [user?.uid, selectedRestaurantId]);

  const loadTables = async () => {
    if (!user?.uid || !selectedRestaurantId) return;
    try {
      setLoading(true);
      const res = await tablesService.getTables(user.uid, selectedRestaurantId);
      setTables(Array.isArray(res) ? res : []);
    } catch (error) {
      console.error('Error cargando mesas:', error);
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        setErrorModal({ open: true, title: 'No se pudieron cargar las mesas', message: error.message });
      } else {
        setErrorModal({ open: true, title: 'Error del servidor', message: 'No se pudieron cargar las mesas' });
      }
      setTables([]);
    } finally {
      setLoading(false);
    }
  };

  const loadWaiters = async () => {
    if (!user?.uid || !selectedRestaurantId) return;
    try {
      const res = await waitersService.getWaiters(user.uid, selectedRestaurantId);
      setWaiters(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      console.error('Error cargando meseros:', error);
      setWaiters([]);
    }
  };

  const handleAddTable = () => {
    const maxNumber = tables.reduce((max, t) => Math.max(max, Number(t.number) || 0), 0);
    const nextNumber = maxNumber + 1;
    const defaultLocation = (selectedLocation !== 'all' ? selectedLocation : 'interior') as 'interior' | 'exterior' | 'barra' | 'vip' | 'terraza';
    setNewTableData({ number: nextNumber, capacity: 4, location: defaultLocation });
    setEditingTable(null);
    setShowAddModal(true);
  };

  const handleCreateTable = async () => {
    if (!user?.uid || !selectedRestaurantId) return;
    try {
      setLoading(true);
      const newTable = await tablesService.createTable(user.uid, selectedRestaurantId, newTableData) as Table;
      setShowAddModal(false);
      
      // Generar QR automáticamente como en el proyecto original
      // const tableInfo = {
      //   tableId: newTable.id,
      //   tableNumber: newTable.number,
      //   capacity: newTable.capacity,
      //   location: newTable.location,
      //   status: newTable.status,
      //   restaurantId: selectedRestaurantId,
      //   restaurantName: '', // Se puede obtener del contexto si es necesario
      //   createdAt: new Date().toISOString(),
      //   lastOrder: null
      // };
      
      //const jsonString = JSON.stringify(tableInfo);
      //const base64Data = btoa(jsonString);
      //const qrUrl = `${window.location.origin}/menu?table=${base64Data}`;
      
      // Mostrar opción de ver QR con modal bonito
      setConfirmViewQR({ open: true, table: newTable });
      
      loadTables();
    } catch (error) {
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        setErrorModal({ open: true, title: 'No se pudo crear la mesa', message: error.message });
      } else {
        setErrorModal({ open: true, title: 'Error del servidor', message: 'No se pudo crear la mesa' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditTable = (table: Table) => {
    setEditingTable(table);
    setNewTableData({
      number: table.number,
      capacity: table.capacity,
      location: table.location as 'interior' | 'exterior' | 'barra' | 'vip' | 'terraza',
    });
    setShowAddModal(true);
  };

  const handleUpdateTable = async () => {
    if (!user?.uid || !selectedRestaurantId || !editingTable) return;
    try {
      setLoading(true);
      await tablesService.updateTable(
        user.uid,
        selectedRestaurantId,
        editingTable.id,
        newTableData
      );
      setShowAddModal(false);
      loadTables();
    } catch (error) {
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        setErrorModal({ open: true, title: 'No se pudo actualizar', message: error.message });
      } else {
        setErrorModal({ open: true, title: 'Error del servidor', message: 'No se pudo actualizar la mesa' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTable = async (table: Table) => {
    if (!user?.uid || !selectedRestaurantId) return;
    if (confirm(`¿Estás seguro de eliminar la mesa ${table.number}?`)) {
      try {
        setLoading(true);
        await tablesService.deleteTable(user.uid, selectedRestaurantId, table.id);
        loadTables();
      } catch (error) {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          setErrorModal({ open: true, title: 'No se pudo eliminar', message: error.message });
        } else {
          setErrorModal({ open: true, title: 'Error del servidor', message: 'No se pudo eliminar la mesa' });
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleToggleStatus = async (table: Table) => {
    if (!user?.uid || !selectedRestaurantId) return;
    try {
      setLoading(true);
      const newStatus = table.status === 'active' ? 'inactive' : 'active';
      await tablesService.updateTable(
        user.uid,
        selectedRestaurantId,
        table.id,
        { status: newStatus }
      );
      loadTables();
    } catch (error) {
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        setErrorModal({ open: true, title: 'No se pudo cambiar el estado', message: error.message });
      } else {
        setErrorModal({ open: true, title: 'Error del servidor', message: 'No se pudo cambiar el estado de la mesa' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewQR = (table: Table) => {
    setSelectedTableForQR(table);
    setShowQRModal(true);
  };

  const handleAssignWaiter = (table: Table) => {
    setSelectedTableForAssignment(table);
    setSelectedWaiterId('');
    setShowAssignmentModal(true);
  };

  const handleConfirmAssignment = async () => {
    if (!user?.uid || !selectedRestaurantId || !selectedTableForAssignment || !selectedWaiterId) return;
    
    try {
      setAssigning(true);
      
      const assignment: TableWaiterAssignment = {
        table_id: selectedTableForAssignment.id,
        waiter_id: selectedWaiterId,
        restaurant_id: selectedRestaurantId
      };
      
      await tablesService.assignTablesToWaiters(user.uid, selectedRestaurantId, [assignment]);
      
      setShowAssignmentModal(false);
      
    } catch (error) {
      console.error('Error asignando mesero:', error);
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        setErrorModal({ open: true, title: 'Error al asignar mesero', message: error.message });
      } else {
        setErrorModal({ open: true, title: 'Error del servidor', message: 'Error al asignar mesero' });
      }
    } finally {
      setAssigning(false);
    }
  };

  // Filtrar mesas
  const filteredTables = useMemo(() => {
    let filtered = tables;

    if (selectedLocation !== 'all') {
      filtered = filtered.filter(table => table.location === selectedLocation);
    }

    if (selectedStatus !== 'all') {
      filtered = filtered.filter(table => {
        const occupied = (table as any).is_occupied === true || table.status === 'occupied' || (table.waiters && table.waiters.length > 0);
        const effective = occupied ? 'occupied' : table.status;
        return effective === selectedStatus;
      });
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(table => 
        table.number.toString().includes(query) ||
        table.capacity.toString().includes(query)
      );
    }

    return filtered;
  }, [tables, selectedLocation, selectedStatus, searchQuery]);

  const stats = {
    total: tables.length,
    active: tables.filter(t => t.status === 'active').length,
    occupied: tables.filter(t => t.status === 'occupied' || (t as any).is_occupied === true).length,
    inactive: tables.filter(t => t.status === 'inactive').length,
    maintenance: tables.filter(t => t.status === 'maintenance').length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#e9ecef] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando mesas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#e9ecef]">
      {/* Header ultra compacto - Todo en una fila */}
      <div className="bg-[#22B6B2]/5 border-b border-gray-200 p-3 max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-3 items-center">
          {/* Botones de acción */}
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={() => navigate('/admin/tables/layout')}
              className="bg-indigo-100 text-indigo-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-indigo-200 transition-colors flex items-center gap-2"
            >
              <span>🗺️</span>
              <span className="hidden sm:inline">Mapa de mesas</span>
            </button>
            <button
              onClick={handleAddTable}
              className="bg-[#f7ebd6] text-[#2C4358] px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-[#f0e0c0] transition-colors text-sm sm:text-base font-medium border-2 border-[#f7ebd6]/50 hover:border-[#f0e0c0]"
            >
              Agregar Mesa
            </button>
          </div>
          
          {/* Búsqueda */}
          <div className="flex-1 min-w-0">
            <input
              type="text"
              placeholder="Buscar mesas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>

          {/* Filtros compactos */}
          <div className="flex gap-2 flex-shrink-0">
            {/* Filtro de ubicación */}
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="all">📍 Todas</option>
              {Object.entries(locationLabels).map(([key, label]) => (
                <option key={key} value={key}>
                  {locationIcons[key as keyof typeof locationIcons] || '📍'} {label}
                </option>
              ))}
            </select>

            {/* Filtro de estado */}
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="all">📊 Todos ({stats.total})</option>
              <option value="active">✅ Activas ({stats.active})</option>
              <option value="occupied">🟠 Ocupadas ({stats.occupied})</option>
              <option value="inactive">⏸️ Inactivas ({stats.inactive})</option>
              <option value="maintenance">🔧 Mantenimiento ({stats.maintenance})</option>
            </select>
          </div>
        </div>
      </div>

      {/* Content - Completamente responsive */}
      <div className="p-3 sm:p-5 max-w-7xl mx-auto">
        {filteredTables.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <div className="text-4xl sm:text-6xl mb-4">
              <img 
                src="/assets/images/mesita.png" 
                alt="Mesa" 
                className="w-16 h-16 sm:w-24 sm:h-24 mx-auto object-contain"
              />
            </div>
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
              {searchQuery || selectedLocation !== 'all' || selectedStatus !== 'all' 
                ? 'No se encontraron mesas' 
                : 'No hay mesas'}
            </h3>
            <p className="text-sm sm:text-base text-gray-500 mb-4 sm:mb-6 px-4">
              {searchQuery || selectedLocation !== 'all' || selectedStatus !== 'all'
                ? 'Intenta con otros filtros o términos de búsqueda'
                : 'Agrega la primera mesa para comenzar'
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 auto-rows-fr">
            {filteredTables.map((table) => (
              <TableCard
                key={table.id}
                table={table}
                onEdit={handleEditTable}
                onToggleStatus={handleToggleStatus}
                onDelete={handleDeleteTable}
                onViewQR={handleViewQR}
                onAssignWaiter={handleAssignWaiter}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <TableModal
          visible={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={editingTable ? handleUpdateTable : handleCreateTable}
          editingTable={editingTable}
          newTableData={newTableData}
          setNewTableData={setNewTableData}
        />
      )}

      {/* QR Modal */}
      {showQRModal && selectedTableForQR && (
        <QRGenerator 
          table={selectedTableForQR} 
          onClose={() => {
            setShowQRModal(false);
            setSelectedTableForQR(null);
          }} 
        />
      )}

      {/* Confirm ver QR */}
      <ConfirmModal
        isOpen={!!confirmViewQR.open}
        title="Mesa creada"
        message="Mesa creada exitosamente. ¿Quieres ver el código QR ahora?"
        confirmText="Ver QR"
        onConfirm={() => {
          if (confirmViewQR.table) {
            setSelectedTableForQR(confirmViewQR.table);
            setShowQRModal(true);
          }
          setConfirmViewQR({ open: false, table: null });
        }}
        onCancel={() => setConfirmViewQR({ open: false, table: null })}
      />

      {/* Assignment Modal */}
      {showAssignmentModal && selectedTableForAssignment && (
        <AssignmentModal
          visible={showAssignmentModal}
          onClose={() => setShowAssignmentModal(false)}
          onSubmit={handleConfirmAssignment}
          table={selectedTableForAssignment}
          waiters={waiters}
          selectedWaiterId={selectedWaiterId}
          setSelectedWaiterId={setSelectedWaiterId}
          assigning={assigning}
        />
      )}
      <ErrorModal isOpen={errorModal.open} title={errorModal.title} message={errorModal.message} details={errorModal.details} onClose={() => setErrorModal({ open: false, message: '' })} />
    </div>
  );
} 